import { GraphQLFleetContractsRequester } from '@cobliteam/api-lib';
import type { FleetCompanyContract, FleetCompanyOnboardingStatus } from '@cobliteam/api-lib/src/__generated__/graphql';
import { CONTRACT_READ_PERMISSIONS, usePermissions } from '@cobliteam/permissions';
import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

const FLEET_CONTRACT_KEY = 'FLEET_CONTRACT';
const FIVE_MINUTES = 5 * 60 * 1000;
const graphQLRequester = () => new GraphQLFleetContractsRequester();

const fleetContractsQueryKey = (fleetId: string) => [FLEET_CONTRACT_KEY, 'contracts', fleetId];

/**
 * Única query de `contracts(fleetId)`: o provider de feature flags, o header (boletos) e a área do
 * cliente compartilham a mesma entrada de cache; cada consumidor adapta o formato com `select`.
 */
const useFleetContracts = <TData = FleetCompanyContract[]>(fleetId: string, options: Partial<UseQueryOptions<FleetCompanyContract[], Error, TData>> = {}) => {
  const { isAccessAllowed } = usePermissions();
  const hasPermission = isAccessAllowed(CONTRACT_READ_PERMISSIONS);

  return useQuery<FleetCompanyContract[], Error, TData>({
    queryKey: fleetContractsQueryKey(fleetId),
    queryFn: () => graphQLRequester().getFleetContracts(fleetId),
    retry: false,
    // Consumidores montam em momentos diferentes do boot; sem staleTime cada um refaria a busca.
    staleTime: FIVE_MINUTES,
    ...options,
    enabled: hasPermission && !!fleetId && (options.enabled ?? true),
  });
};

const useFleetCompanyOnboardingStatus = (fleetId: string, options: Partial<UseQueryOptions<FleetCompanyOnboardingStatus>> = {}) => {
  return useQuery<FleetCompanyOnboardingStatus>({
    queryKey: [FLEET_CONTRACT_KEY, 'onboardingStatus', fleetId],
    queryFn: () => graphQLRequester().getFleetCompanyOnboardingStatus(fleetId),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    ...options,
  });
};

export { fleetContractsQueryKey, useFleetCompanyOnboardingStatus, useFleetContracts };
